<template>
  <div class="commission-roles">
    <div class="page-header">
      <h2 class="page-title">服务费角色管理</h2>
      <p class="page-desc">维护服务费分配涉及的角色字典（如跑手/推荐人/代理商/商家/平台）。角色为服务费分配规则的唯一权威来源，新增或修改角色无需改动代码。</p>
      <div class="page-actions">
        <button class="btn btn-primary" @click="openCreate">新增角色</button>
      </div>
    </div>

    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>角色编码</th>
            <th>角色名称</th>
            <th>描述</th>
            <th>排序</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in list" :key="item.id">
            <td>{{ item.id }}</td>
            <td>{{ item.code }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.description || '-' }}</td>
            <td>{{ item.sort_order }}</td>
            <td>
              <span :class="['status-tag', item.status === 1 ? 'status-on' : 'status-off']">
                {{ item.status === 1 ? '启用' : '禁用' }}
              </span>
            </td>
            <td class="actions">
              <button class="edit-btn" @click="openEdit(item)">编辑</button>
              <button class="delete-btn" @click="handleDelete(item)">删除</button>
            </td>
          </tr>
          <tr v-if="!loading && list.length === 0">
            <td colspan="7" class="empty">暂无数据</td>
          </tr>
        </tbody>
      </table>
      <div v-if="loading" class="loading">加载中...</div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="dialogVisible" class="modal-mask" @click.self="closeDialog">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ form.id ? '编辑角色' : '新增角色' }}</h3>
          <button class="modal-close" @click="closeDialog">×</button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>角色编码 <span class="required">*</span></label>
            <input
              v-model.trim="form.code"
              type="text"
              placeholder="如 runner / referrer"
              :disabled="!!form.id"
            />
            <p class="form-tip">唯一标识，建议用英文小写，用于系统关联，创建后不可修改。</p>
          </div>
          <div class="form-item">
            <label>角色名称 <span class="required">*</span></label>
            <input v-model.trim="form.name" type="text" placeholder="如 跑手" />
          </div>
          <div class="form-item">
            <label>描述</label>
            <input v-model.trim="form.description" type="text" placeholder="选填" />
          </div>
          <div class="form-item">
            <label>排序</label>
            <input v-model.number="form.sort_order" type="number" min="0" />
          </div>
          <div class="form-item">
            <label>状态</label>
            <select v-model.number="form.status">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn" @click="closeDialog">取消</button>
          <button class="btn btn-primary" :disabled="submitting" @click="submitForm">
            {{ submitting ? '提交中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="message" :class="['toast', messageType]">{{ message }}</div>
  </div>
</template>

<script>
import {
  getCommissionRoles,
  createCommissionRole,
  updateCommissionRole,
  deleteCommissionRole
} from '@/api/commissionRoles'

export default {
  name: 'CommissionRoles',
  data() {
    return {
      list: [],
      loading: false,
      dialogVisible: false,
      submitting: false,
      form: {
        id: null,
        code: '',
        name: '',
        description: '',
        sort_order: 0,
        status: 1
      },
      message: '',
      messageType: 'success',
      messageTimer: null
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      if (this.messageTimer) clearTimeout(this.messageTimer)
      this.messageTimer = setTimeout(() => {
        this.message = ''
      }, 2500)
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getCommissionRoles()
        this.list = (res.code === 0 ? res.data.list : res.data) || res || []
      } catch (e) {
        this.showMessage('获取角色列表失败', 'error')
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.form = {
        id: null,
        code: '',
        name: '',
        description: '',
        sort_order: 0,
        status: 1
      }
    },
    openCreate() {
      this.resetForm()
      this.dialogVisible = true
    },
    openEdit(item) {
      this.form = {
        id: item.id,
        code: item.code,
        name: item.name,
        description: item.description || '',
        sort_order: item.sort_order,
        status: item.status
      }
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    async submitForm() {
      if (!this.form.code) {
        this.showMessage('请填写角色编码', 'error')
        return
      }
      if (!this.form.name) {
        this.showMessage('请填写角色名称', 'error')
        return
      }
      this.submitting = true
      try {
        const payload = {
          code: this.form.code,
          name: this.form.name,
          description: this.form.description,
          sort_order: this.form.sort_order,
          status: this.form.status
        }
        if (this.form.id) {
          await updateCommissionRole(this.form.id, payload)
          this.showMessage('更新成功')
        } else {
          await createCommissionRole(payload)
          this.showMessage('创建成功')
        }
        this.closeDialog()
        this.fetchList()
      } catch (e) {
        this.showMessage(e.response?.data?.message || '操作失败', 'error')
      } finally {
        this.submitting = false
      }
    },
    async handleDelete(item) {
      if (!confirm(`确认删除角色「${item.name}」？删除后已关联的规则将显示异常。`)) return
      try {
        await deleteCommissionRole(item.id)
        this.showMessage('删除成功')
        this.fetchList()
      } catch (e) {
        this.showMessage(e.response?.data?.message || '删除失败', 'error')
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 20px;
}
.page-title {
  font-size: 20px;
  margin: 0 0 8px;
}
.page-desc {
  margin: 0 0 16px;
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}
.page-actions {
  text-align: right;
}
.table-wrap {
  position: relative;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #eee;
}
.data-table th,
.data-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
  font-size: 14px;
}
.data-table th {
  background: #fafafa;
  font-weight: 600;
}
.data-table .empty {
  text-align: center;
  color: #999;
  padding: 40px 0;
}
.actions .edit-btn,
.actions .delete-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 8px;
  transition: opacity 0.3s;
}
.actions .edit-btn {
  background: #667eea;
  color: #fff;
}
.actions .delete-btn {
  background: #e74c3c;
  color: #fff;
}
.actions .edit-btn:hover,
.actions .delete-btn:hover {
  opacity: 0.8;
}
.status-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.status-on {
  background: #e6f7ff;
  color: #1890ff;
}
.status-off {
  background: #f5f5f5;
  color: #999;
}
.loading {
  padding: 20px;
  text-align: center;
  color: #999;
}
.btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn-primary {
  background: #1890ff;
  border-color: #1890ff;
  color: #fff;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  width: 480px;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}
.modal-header h3 {
  margin: 0;
  font-size: 16px;
}
.modal-close {
  border: none;
  background: none;
  font-size: 22px;
  cursor: pointer;
  color: #999;
}
.modal-body {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
}
.form-item {
  margin-bottom: 16px;
}
.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
}
.form-item .required {
  color: #f5222d;
}
.form-item input,
.form-item select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-tip {
  margin: 6px 0 0;
  font-size: 12px;
  color: #999;
}
.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  text-align: right;
}
.modal-footer .btn {
  margin-left: 12px;
}
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 20px;
  border-radius: 4px;
  color: #fff;
  z-index: 2000;
  font-size: 14px;
}
.toast.success {
  background: #52c41a;
}
.toast.error {
  background: #f5222d;
}
</style>
